// components/CipStackedChart.jsx
import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Get the regional group for a given school/district name
 */
function getRegionalGroup(schoolName, regionalGroups) {
  for (const [groupName, schools] of Object.entries(regionalGroups)) {
    // Check if any school in the group matches (case-insensitive partial match)
    if (schools.some(s =>
      schoolName.toLowerCase().includes(s.toLowerCase()) ||
      s.toLowerCase().includes(schoolName.toLowerCase())
    )) {
      return groupName;
    }
  }
  return "Other";
}

/**
 * CIP Stacked Chart Component
 * Shows CIP codes as rows with stacked horizontal bars representing district enrollment
 * Supports cross-highlighting when hovering over a district
 */
export default function CipStackedChart({
  cipEnrolled,
  listSchools,
  filterSession,
  nontrad,
  yearIndex = 4,
  districtName,
  regionalGroups = {},
  regionalColors = {}
}) {
  const containerRef = useRef(null);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [expandedCips, setExpandedCips] = useState(new Set());
  const [tooltipData, setTooltipData] = useState(null);
  const tooltipRef = useRef(null);

  // Helper to check if a CIP matches the current filter
  const matchesFilter = useCallback((item) => {
    if (filterSession === "nontrad-female") {
      return nontrad?.female?.includes(item.cipcode);
    } else if (filterSession === "nontrad-male") {
      return nontrad?.male?.includes(item.cipcode);
    }
    return item.clusterName === filterSession || item.programArea === filterSession;
  }, [filterSession, nontrad]);

  // Process data: group by CIP, with district breakdowns
  const chartData = useMemo(() => {
    if (!cipEnrolled?.length) return [];

    const filteredCips = cipEnrolled.filter(matchesFilter);

    return filteredCips.map(cip => {
      // Get enrollment by district (exclude "Total" aggregate row)
      const districtEnrollments = (cip.enrolled || [])
        .filter(school => school.schoolName !== "Total")
        .map(school => ({
          schoolName: school.schoolName,
          enrollment: school.enrollment?.[yearIndex] ?? 0,
          region: getRegionalGroup(school.schoolName, regionalGroups)
        }))
        .filter(d => d.enrollment > 0)
        .sort((a, b) => {
          // Sort by region first, then by enrollment
          const regionOrder = Object.keys(regionalGroups);
          const aIdx = regionOrder.indexOf(a.region);
          const bIdx = regionOrder.indexOf(b.region);
          if (aIdx !== bIdx) return aIdx - bIdx;
          return b.enrollment - a.enrollment;
        });

      const totalEnrollment = districtEnrollments.reduce((sum, d) => sum + d.enrollment, 0);

      return {
        cipcode: cip.cipcode,
        cipcodeName: cip.cipcodeName,
        totalEnrollment,
        districtCount: districtEnrollments.length,
        districts: districtEnrollments
      };
    })
    .filter(cip => cip.totalEnrollment > 0)
    .sort((a, b) => b.totalEnrollment - a.totalEnrollment);
  }, [cipEnrolled, matchesFilter, yearIndex, regionalGroups]);

  // Calculate max enrollment for scale
  const maxEnrollment = useMemo(() => {
    return Math.max(...chartData.map(c => c.totalEnrollment), 1);
  }, [chartData]);

  // Summary stats
  const summary = useMemo(() => {
    const uniqueDistricts = new Set();
    chartData.forEach(cip => {
      cip.districts.forEach(d => uniqueDistricts.add(d.schoolName));
    });

    const totalEnrollment = chartData.reduce((sum, c) => sum + c.totalEnrollment, 0);

    return {
      cipCount: chartData.length,
      districtCount: uniqueDistricts.size,
      totalEnrollment
    };
  }, [chartData]);

  // Region legend data
  const regionLegend = useMemo(() => {
    return Object.entries(regionalGroups).map(([name]) => ({
      name,
      color: regionalColors[name] || '#9ca3af'
    }));
  }, [regionalGroups, regionalColors]);

  // Toggle CIP expansion
  const toggleCip = useCallback((cipcode) => {
    setExpandedCips(prev => {
      const next = new Set(prev);
      if (next.has(cipcode)) {
        next.delete(cipcode);
      } else {
        next.add(cipcode);
      }
      return next;
    });
  }, []);

  // Render a single CIP row with D3
  const renderCipBar = useCallback((svgElement, cip) => {
    const svg = select(svgElement);
    svg.selectAll('*').remove();

    const width = 500;
    const height = 28;
    const barHeight = 20;
    const barY = (height - barHeight) / 2;

    const scale = scaleLinear().domain([0, maxEnrollment]).range([0, width]);

    // Draw stacked segments
    let xOffset = 0;
    cip.districts.forEach((district, idx) => {
      const segmentWidth = scale(district.enrollment);
      const color = regionalColors[district.region] || '#9ca3af';
      const isHovered = hoveredDistrict === district.schoolName;
      const isMuted = hoveredDistrict && !isHovered;

      svg.append('rect')
        .attr('x', xOffset)
        .attr('y', barY)
        .attr('width', segmentWidth)
        .attr('height', barHeight)
        .attr('fill', color)
        .attr('opacity', isMuted ? 0.2 : (isHovered ? 1 : 0.8))
        .attr('stroke', isHovered ? '#000' : 'transparent')
        .attr('stroke-width', isHovered ? 2 : 0)
        .attr('rx', idx === 0 ? 2 : 0)
        .style('cursor', 'pointer')
        .on('mouseenter', function(event) {
          setHoveredDistrict(district.schoolName);
          setTooltipData({
            x: event.clientX,
            y: event.clientY,
            schoolName: district.schoolName,
            enrollment: district.enrollment,
            region: district.region,
            cipName: cip.cipcodeName,
            total: cip.totalEnrollment
          });
        })
        .on('mousemove', function(event) {
          setTooltipData(prev => prev ? { ...prev, x: event.clientX, y: event.clientY } : null);
        })
        .on('mouseleave', function() {
          setHoveredDistrict(null);
          setTooltipData(null);
        });

      // Add thin separator between segments
      if (idx < cip.districts.length - 1 && segmentWidth > 1) {
        svg.append('line')
          .attr('x1', xOffset + segmentWidth)
          .attr('x2', xOffset + segmentWidth)
          .attr('y1', barY)
          .attr('y2', barY + barHeight)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1)
          .style('pointer-events', 'none');
      }

      xOffset += segmentWidth;
    });

    // Background for empty space
    if (xOffset < width) {
      svg.append('rect')
        .attr('x', xOffset)
        .attr('y', barY)
        .attr('width', width - xOffset)
        .attr('height', barHeight)
        .attr('fill', '#f3f4f6')
        .attr('rx', cip.districts.length === 0 ? 2 : 0);
    }
  }, [maxEnrollment, regionalColors, hoveredDistrict]);

  // Re-render bars when hover state changes
  useEffect(() => {
    chartData.forEach(cip => {
      const svgEl = document.getElementById(`cip-bar-${cip.cipcode}`);
      if (svgEl) {
        renderCipBar(svgEl, cip);
      }
    });
  }, [chartData, renderCipBar, hoveredDistrict]);

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-12 text-center">
        <div className="text-zinc-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-zinc-700 mb-2">No enrollment data</h3>
        <p className="text-sm text-zinc-500">No CIP codes with enrollment found for this filter.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Summary KPIs */}
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-6">
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-zinc-800">{summary.cipCount}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1">CIP Codes</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600">{summary.totalEnrollment.toLocaleString()}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1">Total Enrollment</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-zinc-800">{summary.districtCount}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1">Districts</div>
          </div>
        </div>

        {/* Region Legend */}
        <div className="flex items-center justify-center gap-6 pt-4 border-t border-zinc-200">
          {regionLegend.map(({ name, color }) => (
            <div key={name} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
              <span className="text-sm text-zinc-600">{name}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-400" />
            <span className="text-sm text-zinc-600">Other</span>
          </div>
        </div>
      </div>

      {/* CIP Rows */}
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 grid grid-cols-12 gap-4 text-xs font-semibold text-zinc-600 uppercase tracking-wide">
          <div className="col-span-4">CIP Code</div>
          <div className="col-span-6">Enrollment by District</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-right">Districts</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-zinc-100">
          {chartData.map(cip => {
            const isExpanded = expandedCips.has(cip.cipcode);

            return (
              <div key={cip.cipcode}>
                {/* Main Row */}
                <div
                  className={`px-4 py-3 grid grid-cols-12 gap-4 items-center cursor-pointer hover:bg-zinc-50 transition-colors ${
                    isExpanded ? 'bg-zinc-50' : ''
                  }`}
                  onClick={() => toggleCip(cip.cipcode)}
                >
                  <div className="col-span-4 flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate" title={cip.cipcodeName}>
                        {cip.cipcodeName}
                      </div>
                      <div className="text-xs text-zinc-500">{cip.cipcode}</div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <svg
                      id={`cip-bar-${cip.cipcode}`}
                      width={500}
                      height={28}
                      className="overflow-visible"
                    />
                  </div>
                  <div className="col-span-1 text-right text-sm font-semibold text-zinc-800">
                    {cip.totalEnrollment.toLocaleString()}
                  </div>
                  <div className="col-span-1 text-right text-sm text-zinc-600">
                    {cip.districtCount}
                  </div>
                </div>

                {/* Expanded District List */}
                {isExpanded && (
                  <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100">
                    <div className="ml-6 grid grid-cols-3 gap-4">
                      {Object.entries(regionalGroups).map(([regionName]) => {
                        const regionDistricts = cip.districts.filter(d => d.region === regionName);
                        if (regionDistricts.length === 0) return null;

                        return (
                          <div key={regionName} className="space-y-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: regionalColors[regionName] }}
                              />
                              <span className="text-xs font-semibold text-zinc-700">{regionName}</span>
                            </div>
                            {regionDistricts.map(d => (
                              <div
                                key={d.schoolName}
                                className={`flex justify-between items-center text-sm px-2 py-1 rounded transition-colors ${
                                  hoveredDistrict === d.schoolName
                                    ? 'bg-indigo-100'
                                    : 'hover:bg-zinc-100'
                                }`}
                                onMouseEnter={() => setHoveredDistrict(d.schoolName)}
                                onMouseLeave={() => setHoveredDistrict(null)}
                              >
                                <span className="text-zinc-700">{d.schoolName}</span>
                                <span className="font-medium text-zinc-900">{d.enrollment}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      {/* Other region */}
                      {cip.districts.filter(d => d.region === "Other").length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 rounded bg-gray-400" />
                            <span className="text-xs font-semibold text-zinc-700">Other</span>
                          </div>
                          {cip.districts.filter(d => d.region === "Other").map(d => (
                            <div
                              key={d.schoolName}
                              className={`flex justify-between items-center text-sm px-2 py-1 rounded transition-colors ${
                                hoveredDistrict === d.schoolName
                                  ? 'bg-indigo-100'
                                  : 'hover:bg-zinc-100'
                              }`}
                              onMouseEnter={() => setHoveredDistrict(d.schoolName)}
                              onMouseLeave={() => setHoveredDistrict(null)}
                            >
                              <span className="text-zinc-700">{d.schoolName}</span>
                              <span className="font-medium text-zinc-900">{d.enrollment}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {tooltipData && (
        <div
          ref={tooltipRef}
          className="fixed z-50 bg-white border border-zinc-200 rounded-lg shadow-lg p-3 pointer-events-none"
          style={{
            left: tooltipData.x + 12,
            top: tooltipData.y + 12,
            minWidth: 200
          }}
        >
          <div className="font-semibold text-zinc-900 mb-1">{tooltipData.schoolName}</div>
          <div className="flex items-center gap-2 text-sm mb-2">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: regionalColors[tooltipData.region] || '#9ca3af' }}
            />
            <span className="text-zinc-600">{tooltipData.region}</span>
          </div>
          <div className="text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Enrollment:</span>
              <span className="font-semibold text-zinc-900">{tooltipData.enrollment.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-zinc-500">
              <span>of {tooltipData.cipName}:</span>
              <span>{((tooltipData.enrollment / tooltipData.total) * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
